import { useLayoutEffect, useState } from 'react';
import { Platform, Pressable, Text as RNText, TextInput, View } from 'react-native';

import { Text } from '@/components/ui/Text';
import { DESCRIPTION_FIELD_KEY } from '@/lib/presence/fieldKeys';

import { blockedInputProps } from './fieldPresence/blockedInputProps';
import { blockedPressableProps } from './fieldPresence/blockedPressableProps';
import { FieldClaimFrame } from './fieldPresence/FieldClaimFrame';
import { GatedAddAffordance } from './fieldPresence/GatedAddAffordance';
import { useClaimableDraft } from './fieldPresence/useClaimableDraft';
import { useClaimableField } from './fieldPresence/useClaimableField';

/** Inline-editable, auto-growing template description with add/remove states. */
export function EditableDescription({
  initial,
  onCommit,
}: {
  initial: string | null;
  onCommit: (d: string | null) => void;
}) {
  const field = useClaimableField(DESCRIPTION_FIELD_KEY);
  const { inputRef, blocked, inputRemoved } = field;
  const {
    draft: description,
    setDraft: setDescription,
    onFocus,
    onBlur,
  } = useClaimableDraft<string | null>(
    field,
    initial && initial.trim() ? initial : null,
    (draft) => {
      const next = draft?.trim() || null;
      if (next !== (initial?.trim() || null)) onCommit(next);
    },
  );
  const [inputHeight, setInputHeight] = useState<number | undefined>(undefined);

  // Web <textarea> won't shrink once a height is set, so reset to auto and
  // re-measure scrollHeight after each content change. Native uses
  // onContentSizeChange (below), which reports shrinks correctly.
  useLayoutEffect(() => {
    if (Platform.OS !== 'web' || description == null) return;
    const node = inputRef.current as unknown as HTMLTextAreaElement | null;
    if (node?.style) {
      node.style.height = 'auto';
      node.style.height = `${node.scrollHeight}px`;
    }
  }, [description, inputRef]);

  if (description == null) {
    return (
      <GatedAddAffordance
        label="+ Add description"
        field={field}
        onPress={() => setDescription('')}
      />
    );
  }

  return (
    <FieldClaimFrame field={field}>
      <View className="gap-1 rounded-lg bg-well px-3 py-2">
        <View className="flex-row items-center justify-between">
          <Text variant="accent" className="text-[10px]">
            Description
          </Text>
          <Pressable
            onPress={() => {
              setDescription(null);
              inputRemoved();
              if (initial && initial.trim()) onCommit(null);
            }}
            hitSlop={6}
            {...blockedPressableProps(blocked)}
          >
            <RNText className="font-sans text-xs text-faint">Remove</RNText>
          </Pressable>
        </View>
        <TextInput
          ref={inputRef}
          value={description}
          onChangeText={setDescription}
          {...blockedInputProps(blocked)}
          onContentSizeChange={
            Platform.OS === 'web'
              ? undefined
              : (e) => setInputHeight(e.nativeEvent.contentSize.height)
          }
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder="What this template is for"
          placeholderTextColorClassName="accent-faint"
          multiline
          maxLength={500}
          style={
            Platform.OS === 'web' || inputHeight == null
              ? undefined
              : { height: inputHeight }
          }
          className="font-sans text-sm text-foreground-secondary"
        />
      </View>
    </FieldClaimFrame>
  );
}
